import {
  Box,
  Center,
  CircularProgress,
  Collapse,
  Flex,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react'
import { ScreenContext, ThreadSearchProvider, useThreadView } from '@hb/react'
import { colors, ThreadType } from '@hb/shared'
import { ReactElement, useContext, useEffect, useRef } from 'react'
import { AddMembersView } from './AddMembersView'
import { useWebThreadView, WebThreadViewProvider } from './context'
import { ThreadMessagesList } from './Messages/ThreadMessageList'
import { SendMessage } from './SendMessage'
import './styles.css'
import { ThreadBackButton } from './ThreadBackButton'
import { ThreadHeader } from './ThreadHeader'
import { ThreadMuteButton } from './ThreadMuteButton'
import { ThreadSearch, ThreadSearchButton } from './ThreadSearchPopover'
import { ThreadUnreadButton } from './ThreadUnreadButton'

// const ThreadUnreadIndicator = () => (
//   <Box
//     position="absolute"
//     top="-1px"
//     right="-1px"
//     borderRadius="full"
//     bg="white"
//     boxShadow={'0 0 5px rgba(0,0,0,0.5)'}
//     w="13px"
//     h="13px"
//   />
// )
const ThreadViewBody = ({ width }: { width: number }) => {
  const { isMobile } = useContext(ScreenContext)
  const {
    threadId,
    threadType,
    assessmentThreadUnread,
    messages,
    thread: { data: thread, loading: threadLoading },
    addingMembers,
    setAddingMembers,
    searchOpen,
    setSearchOpen,
    manuallyMarkedUnread,
    setManuallyMarkedUnread,
    isOpen,
  } = useThreadView()

  const { messageListRef, messageListHeight, bodyHeight, containerRef, bodyRef } =
    useWebThreadView()

  const { data: sortedMessages, loadingBaseGroups: messagesLoading } = messages

  const searchInputRef = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    if (searchOpen) {
      searchInputRef.current?.focus()
    }
  }, [searchOpen])

  let body: ReactElement | null = null
  if (threadLoading || (messagesLoading && !sortedMessages.length)) {
    body = (
      <Center w="100%" py={3}>
        <HStack spacing={3}>
          <CircularProgress size={5} isIndeterminate color={colors.green.hex} />
          <Text color="gray.500">Loading messages...</Text>
        </HStack>
      </Center>
    )
  } else if (!sortedMessages?.length) {
    body = (
      <Center w="100%">
        <Text px={3} color="gray.500" py={1}>
          No messages yet
        </Text>
      </Center>
    )
  } else {
    body = (
      <ThreadMessagesList
        data={messages}
        threadOpen={isOpen}
        assessmentThreadUnread={assessmentThreadUnread}
        thread={thread}
        ref={messageListRef}
        threadId={threadId}
        manuallyMarkedUnread={manuallyMarkedUnread}
      />
    )
  }
  return (
    <VStack
      borderRadius={6}
      boxShadow="0 0 4px rgba(0,0,0,0.5)"
      // bg="gray.50"
      ref={containerRef}
      height={messageListHeight}
      w={width}
      position="relative"
      bg="gray.100"
      spacing={0}>
      <ThreadHeader />
      <Box bg="gray.100" w="100%" minH="0" overflow="hidden" ref={bodyRef} flex={1}>
        <Collapse style={{ width: '100%' }} in={!addingMembers}>
          <Box w="100%" height={`${bodyHeight}px`}>
            {body}
          </Box>
        </Collapse>
        <Collapse style={{ width: '100%' }} in={addingMembers}>
          <AddMembersView
            height={bodyHeight}
            onClose={() => setAddingMembers(false)}
            thread={thread}
            isOpen={addingMembers}
            threadType={threadType}
            threadId={threadId}
          />
        </Collapse>
      </Box>
      <SendMessage threadId={threadId} threadType={threadType} />
      <ThreadSearchProvider
        onClose={() => setSearchOpen(false)}
        isOpen={searchOpen}
        onOpen={() => setSearchOpen(true)}>
        <Flex
          gap={2}
          flexFlow={isMobile ? 'row' : 'column'}
          position="absolute"
          bottom={isMobile ? '-50px' : 'unset'}
          top={isMobile ? 'unset' : 4}
          right={isMobile ? 2 : '-50px'}>
          <ThreadBackButton />
          <ThreadSearchButton />
          <ThreadUnreadButton
            manuallyMarkedUnread={manuallyMarkedUnread}
            setManuallyMarkedUnread={setManuallyMarkedUnread}
          />
          <ThreadMuteButton />
        </Flex>
        <ThreadSearch ref={searchInputRef} />
      </ThreadSearchProvider>
    </VStack>
  )
}

export const ThreadView = ({
  onBack,
  threadId,
  uid,
  width,
  threadType,
  assessmentThreadUnread,
  isOpen,
  inPopover,
}: {
  onBack: () => void
  threadId?: string
  uid: string | null
  inPopover?: boolean
  width: number
  threadType: ThreadType
  isOpen: boolean
  assessmentThreadUnread?: boolean
}) => {
  const bodyRef = useRef<HTMLDivElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const messageListRef = useRef<HTMLDivElement | null>(null)

  return (
    <WebThreadViewProvider
      isOpen={isOpen}
      onBack={onBack}
      width={width}
      threadType={threadType}
      threadId={threadId}
      uid={uid}
      inPopover={inPopover}
      assessmentThreadUnread={assessmentThreadUnread}
      bodyRef={bodyRef}
      containerRef={containerRef}
      messageListRef={messageListRef}>
      <ThreadViewBody width={width} />
    </WebThreadViewProvider>
  )
}
